import { apiService } from "./api";
import type {
  JobcardManpowerAllocationData,
  JobcardManpowerEmployee,
  JobcardManpowerJobLineItems,
  JobcardManpowerRateBasis,
  JobcardManpowerSavePayload,
} from "../types/jobcardManpower";
import { getCurrentMonthRateBasis } from "../utils/jobcardManpowerUtils";

const AUTH = { includeAuthToken: true, errorHandling: "throw" as const };

export type JobcardManpowerAllocationResult = JobcardManpowerAllocationData & {
  rateBasis: JobcardManpowerRateBasis;
  jobcardDetails: Record<string, unknown>;
  jobcardLineItems: JobcardManpowerJobLineItems;
};

const EMPTY_LINE_ITEMS: JobcardManpowerJobLineItems = {
  labour: [],
  spares: [],
  miscellaneous: [],
};

function parseJobcardLineItems(raw: unknown): JobcardManpowerJobLineItems {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return EMPTY_LINE_ITEMS;
  }
  const o = raw as Record<string, unknown>;
  return {
    labour: Array.isArray(o.labour) ? (o.labour as JobcardManpowerJobLineItems["labour"]) : [],
    spares: Array.isArray(o.spares) ? (o.spares as JobcardManpowerJobLineItems["spares"]) : [],
    miscellaneous: Array.isArray(o.miscellaneous)
      ? (o.miscellaneous as JobcardManpowerJobLineItems["miscellaneous"])
      : [],
  };
}

function parseAllocationResponse(
  res: Record<string, unknown> | null | undefined,
  jobcardId: string
): JobcardManpowerAllocationResult {
  const top = res ?? {};
  let block: JobcardManpowerAllocationData | null = null;

  if (
    top.header &&
    typeof top.header === "object" &&
    !Array.isArray(top.header)
  ) {
    block = top as unknown as JobcardManpowerAllocationData;
  } else if (
    top.data &&
    typeof top.data === "object" &&
    !Array.isArray(top.data) &&
    ("header" in (top.data as object) || "lines" in (top.data as object))
  ) {
    block = top.data as JobcardManpowerAllocationData;
  }

  const header =
    block?.header && typeof block.header === "object"
      ? (block.header as Record<string, unknown>)
      : { jobcard_id: jobcardId };
  const rawLines = block?.lines;
  const lines = Array.isArray(rawLines) ? rawLines : [];
  const jobcardDetails =
    block?.jobcard_details &&
    typeof block.jobcard_details === "object" &&
    !Array.isArray(block.jobcard_details)
      ? (block.jobcard_details as Record<string, unknown>)
      : {};

  const jobcardLineItems = parseJobcardLineItems(
    block?.jobcard_line_items ?? (top.data as Record<string, unknown> | undefined)?.jobcard_line_items
  );

  return {
    header: header as JobcardManpowerAllocationData["header"],
    lines: lines as JobcardManpowerAllocationData["lines"],
    jobcardDetails,
    jobcardLineItems,
    rateBasis:
      (top.rate_basis as JobcardManpowerRateBasis | undefined) ??
      getCurrentMonthRateBasis(),
  };
}

export async function fetchJobcardManpowerAllocation(
  jobcardId: string,
  jobcardNo?: string
): Promise<JobcardManpowerAllocationResult> {
  const params: Record<string, string> = {};
  if (jobcardNo?.trim()) {
    params.jobcard_no = jobcardNo.trim();
  }
  const res = await apiService.get<Record<string, unknown>>(
    `/api/jobcard-manpower/${encodeURIComponent(jobcardId)}`,
    Object.keys(params).length ? params : undefined,
    AUTH
  );
  return parseAllocationResponse(res, jobcardId);
}

export type JobcardManpowerEmployeesResult = {
  employees: JobcardManpowerEmployee[];
  rateBasis: JobcardManpowerRateBasis;
};

export async function fetchJobcardManpowerEmployees(
  search = ""
): Promise<JobcardManpowerEmployeesResult> {
  const res = await apiService.get<{
    success?: boolean;
    data?: JobcardManpowerEmployee[];
    rate_basis?: JobcardManpowerRateBasis;
  }>(
    `/api/jobcard-manpower/employees/list?search=${encodeURIComponent(search)}`,
    undefined,
    AUTH
  );
  return {
    employees: res?.data ?? [],
    rateBasis: res?.rate_basis ?? getCurrentMonthRateBasis(),
  };
}

export type JobcardManpowerSaveResponse = {
  success?: boolean;
  message?: string;
  data?: JobcardManpowerAllocationData;
};

export async function saveJobcardManpowerAllocation(
  payload: JobcardManpowerSavePayload
): Promise<JobcardManpowerSaveResponse> {
  return apiService.post("/api/jobcard-manpower/save", payload, AUTH);
}
